/**
 * RealtimeChatScreen — full-duplex CAOS Care voice surface.
 *
 * Shown when the resident is in a call AND realtime mode is enabled. Replaces
 * the legacy turn-based chat UI with a single living voice panel: the orb
 * pulses while CAOS speaks, the transcript lands beneath it, and the user
 * can chime in any time without breaking the flow.
 */
import React, { useEffect, useRef } from "react";
import { urlTransport } from "../lib/useVoiceTransport";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Mic, X, Volume2, AlertCircle } from "lucide-react";
import { useRealtimeVoice } from "../lib/useRealtimeVoice";
import CopyTranscriptButton from "../components/CopyTranscriptButton";
import DemoRoomVisual from "../components/kiosk/demo/DemoRoomVisual";
import DemoStaffChips from "../components/kiosk/demo/DemoStaffChips";
import { DemoTypedInput, DemoResetButton } from "../components/kiosk/demo/DemoControls";

export default function RealtimeChatScreen({
  resident,
  kiosk,
  voiceId,
  transport = "realtime",   // what the kiosk shows (label)
  onEnd,
  onOpenVoicePicker,
  a11yRootClass,
  triggerSource,
  alertId,
  activationId,
  demo = false,          // public demo kiosk: room visual + typed input + reset
  initialText = null,    // a typed message that started this conversation
}) {
  const { status, error, transcript, resting, micLabel, start, stop, sendText, audioElRef } = useRealtimeVoice({
    voice: voiceId,
    transport: urlTransport(),
    residentId: resident?.resident_id,
    kioskId: kiosk?.kiosk_id,
    room: kiosk?.room || resident?.room,
    onEndCall: onEnd,
    triggerSource,
    alertId,
    activationId,
  });
  // Room already owned by another live session (server-side lease) — this
  // instance never touched the mic. Show it briefly, then return the kiosk
  // to idle so normal polling/triggers resume; there's nothing to tear down.
  useEffect(() => {
    if (!["unavailable", "error"].includes(status)) return;
    const t = setTimeout(() => onEnd?.({ retry: true, reason: status }), 2500);
    return () => clearTimeout(t);
  }, [status, onEnd]);
  const localAudioElRef = useRef(null);
  const initialSentRef = useRef(false);
  const live = ["live", "listening", "speaking"].includes(status);
  // A typed message that opened this conversation goes in once the session is live.
  useEffect(() => {
    if (!initialText || initialSentRef.current || !live) return;
    initialSentRef.current = sendText(initialText);
  }, [initialText, live, sendText]);
  const startedRef = useRef(false);

  // Hand the in-DOM audio element to the hook so playback is reliable
  // across browsers (some refuse to play off-DOM media).
  useEffect(() => {
    audioElRef.current = localAudioElRef.current;
  }, [audioElRef]);

  useEffect(() => {
    if (startedRef.current) return;        // StrictMode guard — only ever start once
    startedRef.current = true;
    start();
    return () => { startedRef.current = false; stop("component_unmount"); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orbState =
    resting ? "" :
    status === "speaking" ? "caos-orb-speak" :
    status === "listening" ? "caos-orb-listen" :
    status === "live" ? "caos-orb" : "";

  return (
    <div
      className={`min-h-screen bg-caos-ambient p-6 md:p-10 flex flex-col ${a11yRootClass || ""}`}
      data-testid="kiosk-realtime-screen"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-caos-mute">
            {transport === "cascade" ? "Low-cost voice" : "Full-duplex · OpenAI Realtime"}
          </p>
          <h2 className="font-display text-3xl md:text-5xl font-light text-caos-forest mt-2">
            Aria is here with you.
          </h2>
          {resident && (
            <p className="text-caos-mute mt-1 text-lg" data-testid="kiosk-realtime-resident">
              Room {resident.room} · {resident.name}
            </p>
          )}
          <div className="mt-3 flex items-center gap-2 text-xs uppercase tracking-widest text-caos-forest font-bold">
            <StatusDot status={status} />
            <span data-testid="kiosk-realtime-status">{labelFor(status)}</span>
            {micLabel && (
              <span data-testid="kiosk-realtime-mic-label" className="normal-case tracking-normal text-caos-mute font-normal">
                · Mic: {micLabel}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={onOpenVoicePicker}
            data-testid="kiosk-realtime-voice-btn"
            className="border-2 h-12 rounded-full"
          >
            <Volume2 className="w-4 h-4 mr-2" />
            VOICE: {(voiceId || "shimmer").toUpperCase()}
          </Button>
          <Button
            onClick={() => { stop("ui_end_call_button"); onEnd?.(); }}
            data-testid="kiosk-realtime-end-btn"
            className="bg-caos-terracotta hover:bg-caos-terracotta-dark h-12 rounded-full"
          >
            <X className="w-5 h-5 mr-2" /> End call
          </Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center mt-6">
        <div
          className={`w-44 h-44 rounded-full bg-caos-forest ${orbState} mb-8`}
          data-testid="kiosk-realtime-orb"
        />
        <p className="font-display text-2xl text-caos-forest text-center max-w-xl">
          {resting && "Resting. I'll be quiet — speak any time."}
          {!resting && status === "connecting" && "Connecting…"}
          {!resting && status === "live" && "Speak any time — I'm listening."}
          {!resting && status === "listening" && "Listening…"}
          {!resting && status === "speaking" && "I'm speaking. You can chime in any time."}
          {!resting && status === "error" && "Something went wrong. Try again."}
          {!resting && status === "unavailable" && "Aria is already here with someone right now."}
        </p>
        {error && (
          <p className="mt-3 inline-flex items-center gap-2 text-caos-terracotta text-sm">
            <AlertCircle className="w-4 h-4" /> {error}
          </p>
        )}
      </div>

      {demo && (
        <div className="mt-4 space-y-3" data-testid="demo-call-panel">
          <DemoRoomVisual room={kiosk?.room || resident?.room} compact />
          <DemoStaffChips room={kiosk?.room || resident?.room} />
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[16rem]"><DemoTypedInput onSend={(t) => sendText(t)} /></div>
            <DemoResetButton />
          </div>
        </div>
      )}

      <Card className="border-caos-line bg-white/70 backdrop-blur p-4 max-h-56 overflow-y-auto mt-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-caos-mute">
            <Mic className="w-3.5 h-3.5" /> Transcript
          </div>
          <CopyTranscriptButton turns={transcript} />
        </div>
        {transcript.length === 0 && (
          <p className="text-caos-mute italic text-sm">
            Transcript will appear as you speak. Pure-voice mode — no need to read.
          </p>
        )}
        <div className="space-y-1.5">
          {transcript.map((m, i) => (
            <div
              key={i}
              data-testid={`kiosk-realtime-transcript-${i}`}
              className={m.role === "user" ? "text-caos-ink" : "text-caos-forest"}
            >
              <span className="font-bold uppercase text-[10px] tracking-widest mr-2">
                {m.role === "user" ? "You" : "Aria"}
              </span>
              {m.text}
            </div>
          ))}
        </div>
      </Card>

      {/* Hidden audio sink — must be mounted in the DOM for reliable playback. */}
      <audio ref={localAudioElRef} autoPlay playsInline className="hidden" data-testid="kiosk-realtime-audio" />
    </div>
  );
}

function StatusDot({ status }) {
  const color =
    status === "speaking" ? "bg-caos-terracotta" :
    status === "listening" ? "bg-caos-amber" :
    status === "live" ? "bg-caos-forest" :
    status === "error" ? "bg-red-500" :
    status === "unavailable" ? "bg-caos-amber" : "bg-caos-mute";
  return <span className={`inline-block w-2 h-2 rounded-full ${color} ${status === "live" || status === "listening" ? "animate-pulse" : ""}`} />;
}

function labelFor(status) {
  switch (status) {
    case "connecting": return "Connecting";
    case "live": return "Live · idle";
    case "listening": return "Listening";
    case "speaking": return "Speaking";
    case "error": return "Error";
    case "unavailable": return "Already active";
    default: return "—";
  }
}
